import { APP_CONFIG } from '@/config/app.config';
import { useAuthStore } from '@/store/auth.store';
import { useUiStore } from '@/store/ui.store';
import { logger } from '@/lib/logger';
import { executeSessionExpiredRedirect } from './session-expired';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface ApiRequestOptions {
  url: string; // relative to baseUrl unless absolute
  method?: HttpMethod;
  params?: Record<string, string | number | boolean | undefined>;
  headers?: Record<string, string>;
  body?: unknown; // will be JSON.stringify-ed if not a string/FormData/Blob
  skipAuth?: boolean;
  skipLoader?: boolean; // Skip showing global loader for this request
  signal?: AbortSignal;
  timeoutMs?: number; // default from APP_CONFIG
}

export interface ApiRequestResponse<T> {
  data: T;
  status: number;
  headers: Headers;
}

/**
 * API Error with response data for detailed error handling
 */
export interface ApiErrorData {
  message?: string | string[];
  error?: string;
  statusCode?: number;
  [key: string]: unknown;
}

export class ApiError extends Error {
  status: number;
  data: ApiErrorData | null;

  constructor(message: string, status: number, data: ApiErrorData | null = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }

  /**
   * Get detailed error messages as an array
   * - If message is an array → return as-is
   * - If message is a string with commas → split into array
   * - If message is a string without commas → return single-item array
   */
  get details(): string[] | undefined {
    if (!this.data?.message) {
      return undefined;
    }
    
    // If message is already an array, return it
    if (Array.isArray(this.data.message)) {
      return this.data.message.filter(Boolean);
    }
    
    // If message is a string
    if (typeof this.data.message === 'string') {
      // Split by comma if it contains commas
      if (this.data.message.includes(',')) {
        return this.data.message.split(',').map(s => s.trim()).filter(Boolean);
      }
      // Otherwise return as single-item array
      return [this.data.message];
    }
    
    return undefined;
  }

  /**
   * Get a user-friendly error message
   */
  getDisplayMessage(): string {
    if (this.data?.message) {
      if (Array.isArray(this.data.message)) {
        return this.data.message[0] || this.message;
      }
      return this.data.message;
    }
    return this.message;
  }
}

// Track active API requests for global loader
let activeRequestCount = 0;

function updateLoadingState(increment: boolean) {
  activeRequestCount += increment ? 1 : -1;
  // Safety check: prevent negative count
  if (activeRequestCount < 0) {
    activeRequestCount = 0;
  }
  const isLoading = activeRequestCount > 0;
  useUiStore.getState().setLoading(isLoading);
}

function buildUrl(baseUrl: string, url: string, params?: ApiRequestOptions['params']): string {
  const full = url.startsWith('http') ? url : `${baseUrl}${url}`;
  if (!params) return full;
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null) usp.append(k, String(v));
  });
  const qs = usp.toString();
  return qs ? `${full}${full.includes('?') ? '&' : '?'}${qs}` : full;
}

export const apiRequest = async <T>({
  url,
  method = 'GET',
  params,
  headers,
  body,
  skipAuth,
  skipLoader,
  signal,
  timeoutMs = APP_CONFIG.api.timeout,
}: ApiRequestOptions): Promise<ApiRequestResponse<T>> => {
  const token = useAuthStore.getState().token;

  // Show loader for this request (unless explicitly skipped)
  if (!skipLoader) {
    updateLoadingState(true);
  }

  const resolvedUrl = buildUrl(APP_CONFIG.api.baseUrl, url, params);

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);

  const finalHeaders: Record<string, string> = {
    ...(headers || {}),
  };
  
  // Don't set Content-Type for FormData - browser sets it automatically with boundary
  if (!(body instanceof FormData)) {
    finalHeaders['Content-Type'] = 'application/json';
  }
  
  if (!skipAuth && token) {
    finalHeaders.Authorization = `Bearer ${token}`;
  }

  let requestBody: BodyInit | undefined;
  if (body instanceof FormData || body instanceof Blob || typeof body === 'string') {
    requestBody = body as BodyInit;
  } else if (body !== undefined) {
    requestBody = JSON.stringify(body);
  }

  logger.debug('API Request', { method, url: resolvedUrl, params });

  try {
    const response = await fetch(resolvedUrl, {
      method,
      headers: finalHeaders,
      body: method === 'GET' || method === 'DELETE' ? undefined : requestBody,
      signal: signal ?? controller.signal,
      credentials: 'include',
    });

    clearTimeout(timeout);

    const contentType = response.headers.get('content-type') ?? '';
    const isJson = contentType.includes('application/json');
    const data = (isJson ? await response.json() : await response.text()) as T;

    logger.debug('API Response', { status: response.status, url: resolvedUrl });

    if (!response.ok) {
      // Handle auth errors consistently
      if (response.status === 401 && !skipAuth && token) {
        logger.warn('Unauthorized access - logging out');
        executeSessionExpiredRedirect();
      } else if (response.status === 403) {
        logger.warn('Forbidden access', { url: resolvedUrl });
      }
      
      // Parse error data from response
      const errorData = isJson ? (data as ApiErrorData) : null;
      let errorMessage = `HTTP ${response.status}`;
      if (errorData?.message) {
        if (Array.isArray(errorData.message) && errorData.message.length > 0) {
          errorMessage = errorData.message[0] ?? errorMessage;
        } else if (typeof errorData.message === 'string') {
          errorMessage = errorData.message;
        }
      }
      
      throw new ApiError(errorMessage, response.status, errorData);
    }

    return { data, status: response.status, headers: response.headers };
  } catch (error) {
    const err = error instanceof Error ? error : new Error(String(error));
    logger.error('API Request Error', err);
    throw err;
  } finally {
    // Hide loader when request completes (unless explicitly skipped)
    if (!skipLoader) {
      updateLoadingState(false);
    }
  }
};


